/**
 * Denial Defender — Universal Orthopedic Defensive Utilities
 *
 * CRITICAL: These utilities prevent false-positive resolutions that
 * trigger automated payer denials. No naive .includes() allowed.
 */
import type {
  PatientDemographics,
  ClinicalSeverityData,
  TargetAnatomy,
} from '@/lib/clinicalSeverityTypes';

// ─── UTILITY 1: Universal Negation Checker ───────────────────
/**
 * Returns true if the keyword appears in a negated context.
 * e.g. "Patient refused steroid injection" → isNegated(text, "steroid") === true
 */
export function isNegated(text: string, keyword: string): boolean {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const negationRegex = new RegExp(
    `(?:no|not|denies|denied|refused|failed|without|lack of|declined|absent|negative for|ruled out)\\s+(?:\\w+\\s+){0,5}${escaped}`,
    'i',
  );
  return negationRegex.test(text);
}

// ─── UTILITY 2: Dynamic Anatomical Context Matcher ───────────
/**
 * Maps target anatomy to specific clinical terminology and validates
 * that text references the correct joint. Prevents cross-contamination
 * (e.g., a lumbar epidural resolving a shoulder injection requirement).
 */
const ANATOMY_LEXICON: Record<string, string> = {
  shoulder: '(?:subacromial|glenohumeral|acromioclavicular|rotator\\s*cuff|labrum|intra-articular\\s+shoulder|shoulder)',
  knee: '(?:intra-articular\\s+knee|meniscus|patellofemoral|tibiofemoral|acl|pcl|knee|genicular)',
  hip: '(?:acetabular|femoral\\s+head|trochanteric|hip|iliopsoas)',
  spine: '(?:epidural|facet|foraminal|disc|lumbar|cervical|thoracic|spinal|interbody)',
  foot: '(?:metatarsal|plantar|hallux|midfoot|forefoot|hindfoot|foot|calcaneal)',
  ankle: '(?:ankle|talar|subtalar|malleol|achilles)',
  hand: '(?:carpal|metacarpal|phalangeal|hand|wrist|finger|thumb)',
};

export function matchesAnatomy(text: string, targetAnatomy: TargetAnatomy): boolean {
  if (targetAnatomy === 'unspecified') return true; // No filtering possible
  const pattern = ANATOMY_LEXICON[targetAnatomy];
  if (!pattern) return true; // Unknown anatomy — don't block
  return new RegExp(pattern, 'i').test(text);
}

/**
 * Checks that a procedure keyword appears near the correct anatomy.
 */
export function hasAnatomicalContext(
  text: string,
  procedureKeyword: string,
  anatomy: TargetAnatomy,
): boolean {
  if (anatomy === 'unspecified') return true;
  const pattern = ANATOMY_LEXICON[anatomy];
  if (!pattern) return true;
  const escaped = procedureKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(
    `${pattern}\\s+(?:\\w+\\s+){0,10}${escaped}`,
    'i',
  );
  return regex.test(text);
}

// ─── UTILITY 3: Universal Therapy Threshold Calculator ───────
/**
 * Returns the payer-required PT weeks based on anatomy and patient risk.
 * - Spine: 6 weeks baseline
 * - Arthroscopic shoulder repair: 8 weeks in the current Carelon shoulder pathway
 * - Knee/hip major joints: 12 weeks, scaling to 24 for high-risk
 * - Small joints (foot/ankle/hand): 4 weeks baseline
 */
export function getRequiredPTWeeks(patient: PatientDemographics, anatomy: TargetAnatomy): number {
  if (anatomy === 'spine') return 6;
  if (anatomy === 'foot' || anatomy === 'ankle' || anatomy === 'hand') return 4;
  if (anatomy === 'shoulder') return 8;
  // Major joints: knee, hip
  if (patient.bmi >= 40 || patient.age < 50) return 24;
  return 12;
}

// ─── UTILITY 4: Duration Parser ──────────────────────────────
/**
 * Extracts weeks from a duration string. Handles "6 weeks", "3 months", etc.
 * Returns null if unparseable.
 */
export function parseDurationWeeks(duration: string | null | undefined): number | null {
  if (!duration) return null;
  const d = duration.toLowerCase();

  const weeksMatch = d.match(/(\d+)\s*weeks?/);
  if (weeksMatch) return parseInt(weeksMatch[1]);

  const monthsMatch = d.match(/(\d+)\s*months?/);
  if (monthsMatch) return parseInt(monthsMatch[1]) * 4;

  const bareMatch = d.match(/^(\d+)$/);
  if (bareMatch) return parseInt(bareMatch[1]);

  return null;
}

// ─── UTILITY 5: ICD-10 Laterality & Specificity Validator ────
/**
 * Validates ICD-10 codes for specificity and laterality mismatches.
 * Payers deny claims with unspecified codes or mismatched laterality.
 */
export function validateICD10(
  icdCodes: string[],
  laterality: string,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const code of icdCodes) {
    // Flag likely unspecified codes (common ortho pattern: ends in 0 or 9)
    if (/\.\d+[09]$/.test(code)) {
      errors.push(`Code ${code} may be 'unspecified'. Payers require maximum specificity.`);
    }

    // Laterality mismatch detection (ortho families M23, M75, M76, S82, etc.)
    // In many ortho ICD-10 families: 1 = right, 2 = left
    if (laterality === 'right' && /\.\d+2$/.test(code)) {
      errors.push(`Laterality mismatch: Procedure is Right, but ${code} indicates Left.`);
    }
    if (laterality === 'left' && /\.\d+1$/.test(code)) {
      errors.push(`Laterality mismatch: Procedure is Left, but ${code} indicates Right.`);
    }
  }

  return { valid: errors.length === 0, errors };
}

// ─── UTILITY 6: Build searchable corpus ──────────────────────
/**
 * Concatenates all text fields from ClinicalSeverityData into a
 * single searchable string for fallback pattern matching.
 */
export function buildCorpus(data: ClinicalSeverityData): string {
  const parts: string[] = [];

  if (data.extraction_notes) parts.push(data.extraction_notes);
  if (data.functional_limitations) parts.push(data.functional_limitations.join(' '));

  const rf = data.radiographic_findings;
  if (rf) {
    if (rf.imaging_modality) parts.push(rf.imaging_modality);
    if (rf.severity_grade) parts.push(rf.severity_grade);
    if (rf.specific_findings) parts.push(rf.specific_findings.join(' '));
  }

  if (data.conservative_therapy) {
    data.conservative_therapy.forEach(t => {
      parts.push(t.modality);
      if (t.duration) parts.push(t.duration);
      if (t.outcome) parts.push(t.outcome);
    });
  }

  if (data.injection_history) {
    data.injection_history.forEach(inj => {
      parts.push(inj.type);
      if (inj.location) parts.push(inj.location);
      if (inj.outcome) parts.push(inj.outcome);
    });
  }

  if (data.pain_assessment) {
    if (data.pain_assessment.pain_character) parts.push(data.pain_assessment.pain_character);
  }

  if (data.comorbidities) parts.push(data.comorbidities.join(' '));

  return parts.join(' ').toLowerCase();
}

// ─── UTILITY 7: Derive Patient Demographics ─────────────────
/**
 * Derives PatientDemographics from ClinicalSeverityData, using
 * safe defaults when data is missing.
 */
export function derivePatientDemographics(data: ClinicalSeverityData): PatientDemographics {
  const flags = data.payer_risk_flags;
  return {
    age: data.age ?? 65,
    bmi: data.bmi ?? 28,
    diabetic: data.diabetic ?? false,
    hba1c: data.hba1c ?? undefined,
  };
}

// ─── UTILITY 8: Infer Target Anatomy from CPT ───────────────
/**
 * Maps CPT code ranges to target anatomy for automatic context detection
 * when procedure_context is not explicitly set.
 */
const CPT_ANATOMY_MAP: [RegExp, TargetAnatomy][] = [
  // Shoulder
  [/^(2382[0-9]|2341[0-2]|298[23][0-9]|2982[4-7])$/, 'shoulder'],
  // Knee
  [/^(274[4-6][0-9]|2988[0-3]|20610)$/, 'knee'],
  // Hip
  [/^(2723[5-6]|27130|27132|27134)$/, 'hip'],
  // Spine
  [/^(630[0-9][0-9]|226[0-9][0-9]|631[0-9][0-9])$/, 'spine'],
  // Foot
  [/^(28[0-9]{3})$/, 'foot'],
  // Ankle
  [/^(276[5-9][0-9]|277[0-1][0-9])$/, 'ankle'],
  // Hand
  [/^(26[0-9]{3})$/, 'hand'],
];

export function inferAnatomyFromCPT(cptCode: string): TargetAnatomy {
  const code = cptCode.trim();
  for (const [regex, anatomy] of CPT_ANATOMY_MAP) {
    if (regex.test(code)) return anatomy;
  }
  return 'unspecified';
}
